import { Directive, ElementRef, inject, Input, OnChanges } from '@angular/core';
import { AnnouncementStatusCodes } from '@common/known-types/announcement-status-codes';
import { EventStatusCodes } from '@common/known-types/event-status-codes';
import { PilotageStatusCodes } from '@common/known-types/pilotage-status-codes';
import { PortPlanRevisionStatusCodes } from '@common/known-types/port-plan-revision-status-codes';
import { ProtestStatusCodes } from '@common/known-types/protest-status-codes';
import { VesselVisitStatusCodes } from '@common/known-types/vessel-visit-status-codes';
import { QueryService } from '@common/services/query.service';

@Directive({
    selector: '[pill]',
    standalone: true
})
export class PillDirective implements OnChanges {
    private queryService = inject(QueryService);

    classColors = {
        blue: 'blue-pill',
        red: 'red-pill',
        brown: 'brown-pill',
        darkBlue: 'dark-blue-pill',
        grey: 'grey-pill',
        green: 'green-pill',
        purple: 'purple-pill',
        yellow: 'yellow-pill'
    };

    formatting = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: '600',
        border: '1px solid transparent'
    };
    vesselColors: any[] = [];
    // Status, module & muted input
    @Input() pill: { status: string; module: string };

    constructor(private el: ElementRef) {
        // Apply formatting
        for (const prop in this.formatting) {
            if (Object.prototype.hasOwnProperty.call(this.el.nativeElement.style, prop)) {
                this.el.nativeElement.style[prop] = this.formatting[prop];
            }
        }
    }

    async ngOnChanges() {
        if (this.pill.module === 'VesselColor') {
            await this.queryService
                .query('CodeListItems', {
                    codelistName: 'VesselColor',
                    id: null,
                    name: null,
                    orderBy: 'id',
                    orderDirection: 'asc',
                    skip: 0,
                    take: 50
                })
                .then((res: any) => {
                    this.vesselColors = res.results;
                });
        }
        const host = this.el.nativeElement;
        const oldDot = host.querySelector('.dot');

        if (oldDot) {
            oldDot.remove();
        }
        host.classList.remove(
            this.classColors.green,
            this.classColors.blue,
            this.classColors.brown,
            this.classColors.red,
            this.classColors.yellow,
            this.classColors.darkBlue,
            this.classColors.grey,
            this.classColors.purple
        );

        if (this.pill.status == null || this.pill.status == undefined || this.pill.status === '-') return;

        const statusClass = this.pillClass(this.pill);
        if (statusClass) {
            host.classList.add('badge');

            if (this.pill.module === 'VesselColor') {
                host.style.backgroundColor = this.softenColor(statusClass as string, 0.75);
                host.style.borderColor = statusClass as string;
                host.style.color = '#000';
            } else {
                host.classList.add(statusClass as string);
                const dot = document.createElement('span');
                dot.className = 'dot';
                this.el.nativeElement.prepend(dot);
            }
        }
    }

    private softenColor(hex: string, amount: number = 0.7): string {
        const num = parseInt(hex.replace('#', ''), 16);

        let r = (num >> 16) & 0xff;
        let g = (num >> 8) & 0xff;
        let b = num & 0xff;

        r = Math.round(r + (255 - r) * amount);
        g = Math.round(g + (255 - g) * amount);
        b = Math.round(b + (255 - b) * amount);

        return `rgb(${r}, ${g}, ${b})`;
    }

    private pillClass(input: any): string | null | any {
        const status = input.status?.toUpperCase();
        const module = input.module;

        if (module === 'VesselColor') {
            const color = this.vesselColors.find((v) => v.id === status);
            return color ? color.name : null;
        }

        if (module === 'VesselVisitStatus') {
            switch (status) {
                case VesselVisitStatusCodes.Anchored:
                    return this.classColors.yellow;
                case VesselVisitStatusCodes.Moored:
                    return this.classColors.green;
                case VesselVisitStatusCodes.Cancelled:
                    return this.classColors.grey;
                case VesselVisitStatusCodes.Rejected:
                    return this.classColors.red;
                case VesselVisitStatusCodes.Completed:
                    return this.classColors.brown;
                case VesselVisitStatusCodes.Unmoored:
                    return this.classColors.darkBlue;
                case VesselVisitStatusCodes.Confirmed:
                    return this.classColors.green;
                case VesselVisitStatusCodes.Announced:
                    return this.classColors.blue;
                case VesselVisitStatusCodes.Planned:
                    return this.classColors.purple;
            }
        }

        if (module === 'PilotageAction') {
            switch (status) {
                case PilotageStatusCodes.Moored:
                    return this.classColors.green;
                case PilotageStatusCodes.Unmoored:
                    return this.classColors.yellow;
                case PilotageStatusCodes.Move:
                    return this.classColors.purple;
            }
        }

        if (module === 'ProtestStatus') {
            switch (status) {
                case ProtestStatusCodes.Valid:
                    return this.classColors.green;
                case ProtestStatusCodes.Invalid:
                    return this.classColors.red;
                case ProtestStatusCodes.ValidInvalid:
                    return this.classColors.yellow;
            }
        }

        if (module === 'AnnouncementStatus') {
            switch (status) {
                case AnnouncementStatusCodes.Confirmed:
                    return this.classColors.green;
                case AnnouncementStatusCodes.Rejected:
                    return this.classColors.red;
                case AnnouncementStatusCodes.Cancelled:
                    return this.classColors.brown;
                case AnnouncementStatusCodes.Announced:
                    return this.classColors.blue;
            }
        }

        if (module === 'PortPlanRevisionStatus') {
            switch (status) {
                case PortPlanRevisionStatusCodes.Draft:
                    return this.classColors.blue;
                case PortPlanRevisionStatusCodes.Approved:
                    return this.classColors.green;
                case PortPlanRevisionStatusCodes.Superseded:
                    return this.classColors.brown;
            }
        }

        if (module === 'Event') {
            switch (status) {
                case EventStatusCodes.Moored:
                    return this.classColors.green;
                case EventStatusCodes.Anchored:
                    return this.classColors.blue;
                case EventStatusCodes.Completed:
                    return this.classColors.grey;
                case EventStatusCodes.NearEvent:
                    return this.classColors.grey;
                case EventStatusCodes.MoveMoore:
                    return this.classColors.purple;
                case EventStatusCodes.MoveUnmoored:
                    return this.classColors.purple;
                case EventStatusCodes.Unmoored:
                    return this.classColors.yellow;
            }
        }
        if (module === 'ArsoWeatherWarning') {
            switch (status.toLowerCase()) {
                case 'severe':
                    return this.classColors.red;
                case 'moderate':
                    return this.classColors.yellow;
                case 'minor':
                    return this.classColors.blue;
                case 'extreme':
                    return this.classColors.red;
            }
        }

        return null;
    }
}
